import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ A RENDER-TIME THROW IS VISIBLE ON THE PAGE. It never renders nothing.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `startup.tsx` already covers the failures that happen **before** React mounts, and its own note
 * calls a blank `#root` with a 200 *"the single failure mode this project has paid for most often"*.
 * This is the other half, and it was missing: **React unmounts the whole tree when a render
 * throws.** No boundary means the page goes white, the cause is in the console only, and the
 * symptom is indistinguishable from a stale dependency cache, a failed stylesheet or a bad deploy.
 *
 * ⚠️ **Found the way these things are always found.** A stale Vite prebundle served a
 * `@forecastify/shared` that predated a new `CATALOGUE[…].fields` array, so `fields` was
 * `undefined` and `.map` threw during render. The cache was the *cause*; the white page was the
 * **only symptom**, and it took a prebundle diff to find. With this boundary the same failure reads
 * *"Cannot read properties of undefined (reading 'map')"* on the page, which is a five-second
 * diagnosis instead of a twenty-minute one.
 *
 * ⚠️ **A class component, because there is still no hook for this.** `componentDidCatch` and
 * `getDerivedStateFromError` have no function-component equivalent in React 19 — this is the one
 * place in the app where a class is not a style choice.
 *
 * ⚠️ **Dependency-free markup, like `startup.tsx`.** Whatever threw may be the token layer, the
 * design system, or the stylesheet; the surface that reports it cannot rely on any of them. Inline
 * styles and plain elements only.
 */

interface State {
  readonly error: Error | undefined;
}

export class ErrorBoundary extends Component<{ readonly children: ReactNode }, State> {
  override state: State = { error: undefined };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    /* ⚠️ The component stack is the useful half and React does not put it in the Error. Logged
     * rather than rendered: it is long, and a person reading the page wants the message. */
    // eslint-disable-next-line no-console -- the last place this information exists.
    console.error('a render threw', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === undefined) return this.props.children;

    return (
      <div
        data-render-failure=""
        role="alert"
        style={{
          font: '14px/1.6 ui-sans-serif,system-ui,sans-serif',
          maxWidth: '56ch',
          margin: '12vh auto',
          padding: '0 24px',
          color: '#2a2a33',
        }}
      >
        <h1 style={{ fontSize: '17px', margin: '0 0 8px' }}>A képernyő nem tudott megjelenni</h1>
        <p style={{ margin: '0 0 12px', whiteSpace: 'pre-wrap' }}>{error.message}</p>
        <p style={{ margin: 0, color: '#6b6b78' }}>
          {/* ⚠️ Names the two causes that actually produce this, in the order they occur. A generic
              "something went wrong" would be true and useless — and this text is what somebody
              reads at the moment they have the least context. */}
          Fejlesztés közben ez általában elavult függőség-gyorsítótár: állítsd le a dev szervert,
          töröld az <code>apps/web/node_modules/.vite</code> könyvtárat, építsd újra a workspace
          csomagokat, majd indítsd újra. A teljes hívási lánc a konzolban van.
        </p>
      </div>
    );
  }
}
